import React, { useState, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { useFetchDetail } from "@hooks/useFetchDetail";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ServicesURL, getAllErrorsAsString } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import AnimationFadeIn from "@components/AnimationFadeIn";
import { useAddEditCostProject } from "../hooks/useAddEditCostProject";
import CostProjectFormCst from "../CostProjectFormCst";
import { MethodType } from "src/services/type";
import useSWRMutation from "swr/mutation";
import { fetcher } from "src/services/config";
import ModalCst, { OutcomesInterface } from "@components/ModalCst";
import { isTrueValue } from "@utils/utilsValidators";

interface EditCostProjectInterface {
  onCancelAction: () => void;
  onSubmitAction: (data: any, list?: any) => void;
  idCostEdit?: string;
}

const EditCostProject: React.FC<EditCostProjectInterface> = ({
  onCancelAction,
  onSubmitAction,
  idCostEdit,
}) => {
  const { t } = useTranslation(["projects-cost-page"]);
  const { idProject } = useParams();

  const { columns, dataDetail, mutate } = useFetchDetail(
    `${ServicesURL.getProjectsCost}`,
    idCostEdit,
    PagesID["projects.cost"],
  );

  const { dataDetail: dataProject } = useFetchDetail(
    `${ServicesURL.getProjects}`,
    idProject,
  );

  const {
    handleAssociation,
    setOpenAssociation,
    handleDelete,
    getValues,
    resetCostProjectAssociations,
    setSupplierAssociation,
    setCategoryAssociation,
    setContractAssociation,
    supplierAssociation,
    categoryAssociation,
    contractAssociation,
    openAssociations,
  } = useAddEditCostProject([]);

  // CUSTOM API FOR APPLICATION/JSON-PATCH+JSON
  const { trigger: triggerEditProjectCost } = useSWRMutation(
    `${ServicesURL.getProjectsCost}/${idCostEdit}`,
    (url: string, arg: { arg: any }) =>
      fetcher<any>(`${url}`, MethodType.PUT, { ...arg.arg }),
  );

  const getInitialOutcome = () => {
    return {
      budget: {
        code: undefined,
        message: t("edit.outcome.project.messageLoading"),
        title: t("edit.outcome.project.title"),
      },
    };
  };

  const initialOutcome: OutcomesInterface = {
    budget: {
      code: undefined,
      message: t("edit.outcome.project.messageLoading"),
      title: t("edit.outcome.project.title"),
    },
  };

  const [outcome, setOutcome] = useState<OutcomesInterface>(initialOutcome);
  const [openOutcome, setOpenOutcome] = useState(false);

  const onSubmit = async (inputForm: any) => {
    setOpenOutcome(true);
    const initialOutcomeState: any = getInitialOutcome();
    setOutcome(initialOutcomeState);
    const newOutcome: any = JSON.parse(JSON.stringify(initialOutcomeState));
    const objToSend: any = {
      title: inputForm.title || null,
      iternal: inputForm.internal || null,
      budget_project_categorization_id:
        categoryAssociation && categoryAssociation.length > 0
          ? parseInt(categoryAssociation[0].id)
          : null,
      unit_cost: Number(inputForm.unit_cost) || null,
      qt: Number(inputForm.qt) || null,
      management_type:
        inputForm.management_type === "BC_BE" ||
        inputForm.management_type === "RDA"
          ? inputForm.management_type
          : null,
      supplier_id:
        supplierAssociation && supplierAssociation.length > 0
          ? parseInt(supplierAssociation[0].id)
          : null,
      contract_id:
        contractAssociation && contractAssociation.length > 0
          ? parseInt(contractAssociation[0].id)
          : null,
      funded: dataProject.dto?.funded ? isTrueValue(inputForm.funded) : null,
      person_days: Number(inputForm.person_days) || null,
      notes: inputForm.notes || null,
    };

    try {
      await triggerEditProjectCost(objToSend);
      newOutcome.budget.code = "OK";
      newOutcome.budget.message = t("edit.outcome.project.messageOK");
      setOutcome(newOutcome);
    } catch (error) {
      newOutcome.budget.code = "KO";
      newOutcome.budget.message = getAllErrorsAsString(error);
      setOutcome(newOutcome);
    }
    mutate(inputForm);
  };

  const getColumns = useMemo(() => {
    return columns.map((el) => {
      if (
        el.accessorKey === "funded" ||
        el.accessorKey === "internal" ||
        el.accessorKey === "budget_project_categorization"
      ) {
        return { ...el, disabled: true };
      } else {
        return { ...el };
      }
    });
  }, []);

  return (
    <AnimationFadeIn>
      {columns && (
        <Box
          sx={{
            mt: "25px",
            maxHeight: "100vh",
            overflowY: "auto",
          }}
        >
          <Typography
            variant="h3"
            component="h2"
            sx={{
              mb: "8px",
              textAlign: "center",
            }}
          >
            {t("hero-title-edit")}
          </Typography>

          <CostProjectFormCst
            row={dataDetail || []}
            columns={getColumns}
            onSubmit={onSubmit}
            backButton={() => {
              onCancelAction();
            }}
            setOpenAssociation={(accessorKey, open, inputForm) =>
              setOpenAssociation(accessorKey, open, inputForm)
            }
            handleDelete={(accessorKey, el) => handleDelete(accessorKey, el)}
            handleAssociation={(accessorKey, value) =>
              handleAssociation(accessorKey, value)
            }
            getValues={(accessorKey) => getValues(accessorKey)}
            association={openAssociations}
            onReset={() => {
              resetCostProjectAssociations();
            }}
            resetCostProjectAssociations={resetCostProjectAssociations}
            categoryAssociation={categoryAssociation}
            supplierAssociation={supplierAssociation}
            setSupplierAssociation={setSupplierAssociation}
            setCategoryAssociation={setCategoryAssociation}
            setContractAssociation={setContractAssociation}
            dataProject={dataProject?.dto || []}
            fullWidth={true}
            isEdit={true}
          />
        </Box>
      )}

      <ModalCst
        open={openOutcome}
        setOpen={setOpenOutcome}
        typeOfModal={"outcome"}
        data={{
          outcome: outcome,
          callBackSubmit: () => {
            onCancelAction();
            onSubmitAction(undefined, undefined);
          },
        }}
      />
    </AnimationFadeIn>
  );
};

export default EditCostProject;
